import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import FilterC from '@/gf/components/Filters/Filter'
import Link from '@/gf/components/LinkOld'
import allFilterTypes from '@/gf/data/filterTypes'
import fields from '@/gf/data/productFields'
import useFilters from '@/gf/hooks/useFilters'
import Filter from '@/gf/modules/Filter'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import Money from '@/gf/modules/Money'
import ProductM from '@/gf/modules/Product'
import User from '@/gf/modules/User'
import EqualsFilter from '@/gf/modules/filters/Equals'
import * as api from '@/suppliers/modules/api'
import classNames from 'classnames'
import uniq from 'lodash/uniq'
import React from 'react'
import { EditLineItem, ProductSearchResult } from '../../types'
import useSession from '../hooks/useSession'
import QueryString from '../modules/QueryString'

const filterTypeIds = uniq(fields.flatMap((f) => f.filterTypeIds))
const filterTypes = allFilterTypes.filter((ft) => filterTypeIds.includes(ft.id))

const AddProductFilters = ({
  restrictToStoreId,
  addProduct,
  productsFromQuery,
  newOrderLineItems,
  setProductsFromQuery,
}: {
  restrictToStoreId?: string
  addProduct: (product: ProductSearchResult) => void
  productsFromQuery: ProductSearchResult[]
  newOrderLineItems: EditLineItem[]
  setProductsFromQuery: (products: ProductSearchResult[]) => void
}) => {
  const { user } = useSession()
  const [queryLoading, setQueryLoading] = React.useState(false)
  const [stagedFilters, filtersApi] = useFilters([EqualsFilter.build({ fieldId: 'mpn' })])

  const filterFields = restrictToStoreId
    ? fields.filter((field) => field.id !== 'store_id')
    : fields

  const buildFilter = () => {
    const field = fields[0]
    return Filter.build({ fieldId: field.id, typeId: field.filterTypeIds[0] })
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (queryLoading === false) {
      setQueryLoading(true)
      setProductsFromQuery([])

      const searchFilters = FormatFilters.andFilters(
        FilterSet.toApiFilters(
          restrictToStoreId
            ? [
                ...stagedFilters,
                EqualsFilter.build({ fieldId: 'storeId', text: restrictToStoreId }),
                EqualsFilter.build({ fieldId: 'offline', text: 'false' }),
                EqualsFilter.build({ fieldId: 'enabled', text: 'true' }),
              ]
            : stagedFilters
        )
      )

      api
        .searchProducts({
          filters: searchFilters,
        })
        .then((productsSearch) => {
          const idsInOrder = newOrderLineItems.map((li) => li.product.id)
          const productSearchResults = productsSearch.products.map((product) => ({
            ...product,
            inOrder: idsInOrder.includes(product.id),
          }))

          setProductsFromQuery(productSearchResults)
          setQueryLoading(false)
        })
    }
  }

  return (
    <>
      <Card.Section>
        <div className="min-w-full">
          <form onSubmit={onSubmit}>
            <div className="grid gap-2">
              {stagedFilters.map((filter) => (
                <FilterC
                  key={filter.id}
                  filter={filter}
                  fields={filterFields}
                  filterTypes={filterTypes}
                  filtersApi={filtersApi}
                  canRemove={stagedFilters.length > 1}
                />
              ))}

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => filtersApi.add(buildFilter())}
                  title="➕ Filter"
                  type="button"
                  primary={false}
                />
                <Button title="Search" type="submit" />
              </div>
            </div>
          </form>
        </div>
      </Card.Section>
      {queryLoading && (
        <Card.Section>
          <div className="animate-spin-slow border-t-gearflow ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16 mx-auto" />
        </Card.Section>
      )}
      {productsFromQuery.length > 0 && (
        <Card.Section title="Search Results">
          <div className="max-h-96 overflow-y-scroll">
            {productsFromQuery.map((product) => (
              <Card.SubSection key={product.id}>
                <div className="flex space-x-4">
                  <img
                    src={ProductM.primaryImageURL(product, 'card')}
                    alt={product.name}
                    className="w-16 h-16 border rounded-sm overflow-hidden"
                  />
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm text-gray-900 font-bold">{product.name}</p>
                    <p className="text-sm text-gray-500">Store: {product.store.name}</p>
                    {User.isAdmin(user) ? (
                      <p className="text-sm text-gray-500">
                        MPN:{' '}
                        <Link to={`/orders${QueryString.storeOrderWithMpn(product.mpn)}`}>
                          {product.mpn}
                        </Link>
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">MPN: {product.mpn}</p>
                    )}
                    {User.isAdmin(user) && product.altMpn ? (
                      <p className="text-xs text-gray-500">
                        ALT MPN:{' '}
                        <Link to={`/orders${QueryString.storeOrderWithMpn(product.altMpn)}`}>
                          {product.altMpn}
                        </Link>
                      </p>
                    ) : (
                      <></>
                    )}
                    <p className="text-sm text-gray-500">
                      Price: {Money.format(Money.fromDecimal(product.salePrice, 'USD'))}
                    </p>
                    {product.inOrder && <p className="text-sm text-yellow-500">Item was added.</p>}
                    {!product.enabled && !product.offline && (
                      <p className="text-sm text-red-500">Item is disabled</p>
                    )}
                    {product.offline && (
                      <p className="text-sm text-yellow-500">Item created offline</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    title="Add"
                    className={classNames('self-start', {
                      'text-gray-400 border-gray-300 bg-gray-200 cursor-not-allowed':
                        !product.enabled && !product.offline,
                    })}
                    disabled={!product.enabled && !product.offline}
                    onClick={() => addProduct(product)}
                  />
                </div>
              </Card.SubSection>
            ))}
          </div>
        </Card.Section>
      )}
    </>
  )
}

export default AddProductFilters
